import { BASE_URL } from "config/config";
import React, { useEffect, useState } from "react";
import { Table, Row, Col, Form, Button, Modal } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  useGetCRAByIdQuery,
  useValidateCRAMutation,
  useRefuseCRAMutation,
} from "store/cra/cra.api";

const ValidationPage = () => {
  const { craId } = useParams();

  const { data: craData, isLoading, isError } = useGetCRAByIdQuery(craId);
  const [validateCRA, { isLoading: isValidating }] = useValidateCRAMutation();
  const [refuseCRA, { isLoading: isRefusing }] = useRefuseCRAMutation();

  const [daysInMonth, setDaysInMonth] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!craData?.data?.data) return;

    const cra = craData.data.data;

    // Générer tous les jours du mois
    const days = [];
    const date = new Date(cra.year, cra.month - 1, 1);
    while (date.getMonth() === cra.month - 1) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    setDaysInMonth(days);

    // Récupérer les jours fériés FR
    const fetchHolidays = async () => {
      try {
        const res = await fetch(
          `https://date.nager.at/api/v3/PublicHolidays/${cra.year}/FR`
        );
        const data = await res.json();
        setHolidays(data.map((h) => new Date(h.date).toDateString()));
      } catch (err) {
        console.error(err);
      }
    };
    fetchHolidays();
  }, [craData?.data?.data]);

  if (isLoading) return <div>Chargement...</div>;
  if (isError || !craData?.data?.data)
    return <div>Erreur lors du chargement du CRA</div>;

  const cra = craData.data.data;
  const lineData = cra.lines || {};
  const totalProduction = cra.totalProduction || 0;
  const totalAbsences = cra.totalAbsences || 0;
  const totalIntern = cra.totalIntern || 0;

  const weekDays = ["DI", "LU", "MA", "ME", "JE", "VE", "SA"];

  // Totaux par jour
  const totalPerDay = {};
  let totalWeekends = 0;
  let totalHolidays = 0;

  daysInMonth.forEach((day) => {
    const dayStr = day.toLocaleDateString("fr-CA");
    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
    const isHoliday = holidays.includes(day.toDateString());

    if (isWeekend) totalWeekends += 1;
    if (isHoliday) totalHolidays += 1;

    totalPerDay[dayStr] =
      isWeekend || isHoliday
        ? 1
        : Object.values(lineData).reduce(
            (sum, line) => sum + (Number(line[dayStr]) || 0),
            0
          );
  });

  const grandTotal = Object.values(totalPerDay).reduce((sum, v) => sum + v, 0);

  const handleValidate = async () => {
    try {
      await validateCRA(craId).unwrap();
      alert("CRA validé !");
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la validation");
    }
  };

  const handleRefuse = async () => {
    try {
      await refuseCRA({ craId, reason }).unwrap();
      alert("CRA refusé !");
      setShowModal(false);
      setReason("");
    } catch (err) {
      console.error(err);
      alert("Erreur lors du refus");
    }
  };
  function getImageURL(id) {
    return `${BASE_URL}cra/${id}/image`;
  }

  return (
    <div className="timesheet-container p-3 m-4">
      <h5>
        CRA de {cra.collaborator.name} - {cra.month}/{cra.year}
      </h5>

      {/* Tableau des CRA */}
      <Table bordered hover responsive className="timesheet-table mb-3">
        <thead>
          <tr>
            <th>Jour</th>
            {daysInMonth.map((day) => (
              <th key={day.toDateString()}>
                <div>{weekDays[day.getDay()]}</div>
                <div>{day.getDate()}</div>
              </th>
            ))}
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(lineData).map((lineKey) => (
            <tr key={lineKey}>
              <td>{lineKey}</td>
              {daysInMonth.map((day) => {
                const dayStr = day.toLocaleDateString("fr-CA");
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const isHoliday = holidays.includes(day.toDateString());
                return (
                  <td
                    key={dayStr}
                    className={isWeekend || isHoliday ? "weekend-cell" : ""}>
                    <Form.Control
                      type="text"
                      value={lineData[lineKey][dayStr] || ""}
                      disabled
                    />
                  </td>
                );
              })}
              <td>
                {Object.values(lineData[lineKey]).reduce(
                  (sum, v) => sum + (Number(v) || 0),
                  0
                )}
                j
              </td>
            </tr>
          ))}
          <tr className="fw-bold">
            <td>Total</td>
            {daysInMonth.map((day) => {
              const dayStr = day.toLocaleDateString("fr-CA");
              return <td key={dayStr}>{totalPerDay[dayStr]}j</td>;
            })}
            <td>{grandTotal}j</td>
          </tr>
        </tbody>
      </Table>

      {/* Totaux par catégorie */}
      <Row className="mb-3">
        <Col>
          <div className="totals-box">
            <strong>Production</strong>
            <div>{totalProduction}j</div>
          </div>
        </Col>
        <Col>
          <div className="totals-box">
            <strong>Absences</strong>
            <div>{totalAbsences}j</div>
          </div>
        </Col>
        <Col>
          <div className="totals-box">
            <strong>Intern</strong>
            <div>{totalIntern}j</div>
          </div>
        </Col>
      </Row>

      {/* Résumé global */}
      <Row className="mb-3">
        <Col>
          <div className="totals-box">
            <strong>Résumé global</strong>
            <div>Total jours : {daysInMonth.length}</div>
            <div>Production : {totalProduction}j</div>
            <div>Absences : {totalAbsences}j</div>
            <div>Intern : {totalIntern}j</div>
            <div>Weekends : {totalWeekends}j</div>
            <div>Fériés : {totalHolidays}j</div>
          </div>
        </Col>
      </Row>

      {/* Commentaires et pièces jointes */}
      <Row className="mt-4">
        <Col md={6}>
          <h6>Commentaires</h6>
          <p>{cra.commentaire || "Aucun commentaire fourni."}</p>
        </Col>
        <Col md={6}>
          <h6>Pièces jointes</h6>
          {cra.attachments ? (
            <ul>
              <li>
                <a
                  href={getImageURL(cra._id)}
                  target="_blank"
                  rel="noopener noreferrer">
                  {cra.attachments}
                </a>
              </li>
            </ul>
          ) : (
            <p>Aucune pièce jointe.</p>
          )}
        </Col>
      </Row>

      {/* Boutons Valider / Refuser */}
      <Row className="mt-3">
        <Col>
          <Button
            variant="success"
            onClick={handleValidate}
            disabled={isValidating}>
            {isValidating ? "Validation..." : "Valider le CRA"}
          </Button>{" "}
          <Button variant="danger" onClick={() => setShowModal(true)}>
            Refuser le CRA
          </Button>
        </Col>
      </Row>

      {/* Modal pour le refus */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Refuser le CRA</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Raison du refus</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleRefuse} disabled={isRefusing}>
            {isRefusing ? "Enregistrement..." : "Enregistrer le refus"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ValidationPage;
